import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Stack, CircularProgress, Alert, Grid, Card,
  CardContent, Avatar, Chip, Button, IconButton, Tooltip, alpha, Divider,
  LinearProgress, Dialog, DialogTitle, DialogContent,
  ImageList, ImageListItem, ImageListItemBar, useTheme, useMediaQuery
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { useAuth } from '../context/AuthContext';
import {
  fetchSubscriptionDetail,
  fetchReceivedImages,
  fetchSentImages,
  uploadSentImages,
  deleteSentImage,
  deleteReceivedImage
} from '../designApi';

// ✅ Helper: Convert File to base64 string
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export default function DesignSubscriptionDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const [subscription, setSubscription] = useState(null);
  const [receivedImages, setReceivedImages] = useState([]);
  const [sentImages, setSentImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewDialog, setPreviewDialog] = useState({ open: false, imageUrl: '', title: '' });
  const [deleteLoading, setDeleteLoading] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [subData, recImages, sntImages] = await Promise.all([
        fetchSubscriptionDetail(id),
        fetchReceivedImages(id),
        fetchSentImages(id)
      ]);
      setSubscription(subData);
      // ✅ Backend getReceivedImages returns { images: [...], subscriptionId, totalDesigns, ... }
      // Backend getSentImages returns { images: [...], subscriptionId, totalDesigns, ... }
      const recArr = recImages?.images || (Array.isArray(recImages) ? recImages : []);
      const sntArr = sntImages?.images || (Array.isArray(sntImages) ? sntImages : []);
      setReceivedImages(recArr);
      setSentImages(sntArr);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ✅ FIXED: Backend uploadSentImages expects JSON body: { adminId, images: [base64strings] }
  // NOT FormData. Backend uses saveDesignImage() which parses "data:image/..." base64 strings
  const handleUploadSentImages = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const totalAllowed = (subscription?.NumberOfDesigns || 0) - sentImages.length;
    if (files.length > totalAllowed) {
      setError(`You can only upload ${totalAllowed} more design(s). Currently ${sentImages.length}/${subscription?.NumberOfDesigns} sent.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setUploading(true);
      setError('');

      // ✅ Convert files to base64 strings (backend expects "data:image/..." format)
      const base64Images = await Promise.all(files.map(file => fileToBase64(file)));

      // ✅ Backend requires: { adminId, images: [base64strings] }
      await uploadSentImages(id, {
        adminId: user?.Id || user?.id,
        images: base64Images
      });

      setSuccess(`${files.length} design(s) uploaded successfully`);

      // Reload data
      const [sntImages, subData] = await Promise.all([
        fetchSentImages(id),
        fetchSubscriptionDetail(id)
      ]);
      setSentImages(sntImages?.images || (Array.isArray(sntImages) ? sntImages : []));
      setSubscription(subData);

      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload designs');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteSentImage = async (imageId) => {
    if (!imageId) return;
    try {
      setDeleteLoading(imageId);
      setError('');
      await deleteSentImage(id, imageId);
      setSuccess('Design image deleted successfully');

      const [sntImages, subData] = await Promise.all([
        fetchSentImages(id),
        fetchSubscriptionDetail(id)
      ]);
      setSentImages(sntImages?.images || (Array.isArray(sntImages) ? sntImages : []));
      setSubscription(subData);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete image');
    } finally {
      setDeleteLoading(null);
    }
  };

  // ✅ NEW: Delete received image handler
  // Backend deleteReceivedImage: DELETE /subscriptions/:id/received-images/:imageId
  // Checks: subscription exists, not completed, image exists, decrements UploadedDesignsCount
  const handleDeleteReceivedImage = async (imageId) => {
    if (!imageId) return;
    try {
      setDeleteLoading(`recv-${imageId}`);
      setError('');
      await deleteReceivedImage(id, imageId);
      setSuccess('Received image deleted successfully');

      const [recImages, subData] = await Promise.all([
        fetchReceivedImages(id),
        fetchSubscriptionDetail(id)
      ]);
      setReceivedImages(recImages?.images || (Array.isArray(recImages) ? recImages : []));
      setSubscription(subData);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete received image');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleDownloadImage = (imageUrl, filename) => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename || 'design-image';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getProgress = () => {
    if (!subscription) return { received: 0, sent: 0, total: 1, receivedPercent: 0, sentPercent: 0, isComplete: false };
    const total = subscription.NumberOfDesigns || 1;
    const received = receivedImages.length;
    const sent = sentImages.length;
    return {
      received,
      sent,
      total,
      receivedPercent: Math.min((received / total) * 100, 100),
      sentPercent: Math.min((sent / total) * 100, 100),
      isComplete: subscription.Status === 'completed' || sent >= total
    };
  };

  const progress = getProgress();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (!subscription) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('SubscriptionNotFound') || 'Subscription not found'}</Alert>
        <Button onClick={() => navigate('/admin/design-management')} sx={{ mt: 2 }}>
          {t('GoBack') || 'Go Back'}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha('#6a1b9a', 0.08)} 0%, ${alpha('#ad1457', 0.08)} 100%)`,
          border: `1px solid ${alpha('#6a1b9a', 0.15)}`
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={() => navigate('/admin/design-management')} sx={{ bgcolor: alpha('#6a1b9a', 0.1) }}>
              <ArrowBackIcon sx={{ color: '#6a1b9a' }} />
            </IconButton>
            <Avatar sx={{ bgcolor: '#6a1b9a', width: 50, height: 50 }}>
              <DesignServicesIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="#6a1b9a">
                {t('SubscriptionDetail') || 'Subscription Detail'}
              </Typography>
              <Chip
                label={subscription.SubscriptionId || '-'}
                size="small"
                sx={{ bgcolor: alpha('#6a1b9a', 0.1), color: '#6a1b9a', fontWeight: 700, mt: 0.5 }}
              />
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            {progress.isComplete && (
              <Chip
                icon={<CheckCircleIcon />}
                label={t('Completed') || 'Completed'}
                color="success"
                sx={{ fontWeight: 700 }}
              />
            )}
            <Tooltip title={t('Refresh') || 'Refresh'}>
              <IconButton onClick={loadData} sx={{ bgcolor: alpha('#6a1b9a', 0.1) }}>
                <RefreshIcon sx={{ color: '#6a1b9a' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Subscription Info Card */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha('#000', 0.08)}`, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t('AdminInfo') || 'Admin Info'}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha('#ad1457', 0.1), width: 44, height: 44, fontWeight: 700 }}>
                  {(subscription.Admin?.Name || 'A').charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {subscription.Admin?.Name || `Admin #${subscription.AdminId || '?'}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {subscription.Admin?.Number || ''}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {t('PlanInfo') || 'Plan Info'}
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">{t('Plan') || 'Plan'}</Typography>
                  <Typography variant="body2" fontWeight={600}>{subscription.PlanName || '-'}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">{t('Designs') || 'Designs'}</Typography>
                  <Typography variant="body2" fontWeight={600}>{subscription.NumberOfDesigns || 0}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">{t('Amount') || 'Amount'}</Typography>
                  <Typography variant="body2" fontWeight={700} color="#ad1457">
                    {subscription.FormattedAmount || `${subscription.Amount || 0} ${subscription.Currency || ''}`}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha('#000', 0.08)}`, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t('Progress') || 'Progress'}
              </Typography>

              {/* Received Progress */}
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DownloadIcon sx={{ fontSize: 20, color: '#1565c0' }} />
                    <Typography variant="body2" fontWeight={600} color="#1565c0">
                      {t('ReceivedFromAdmin') || 'Received from Admin'}
                    </Typography>
                  </Stack>
                  <Chip
                    label={`${progress.received} / ${progress.total}`}
                    size="small"
                    sx={{ bgcolor: alpha('#1565c0', 0.1), color: '#1565c0', fontWeight: 700 }}
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress.receivedPercent}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    bgcolor: alpha('#1565c0', 0.1),
                    '& .MuiLinearProgress-bar': { borderRadius: 6, bgcolor: '#1565c0' }
                  }}
                />
              </Box>

              {/* Sent Progress */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SendIcon sx={{ fontSize: 20, color: '#2e7d32' }} />
                    <Typography variant="body2" fontWeight={600} color="#2e7d32">
                      {t('DesignsSentBack') || 'Designs Sent Back'}
                    </Typography>
                  </Stack>
                  <Chip
                    label={`${progress.sent} / ${progress.total}`}
                    size="small"
                    sx={{ bgcolor: alpha('#2e7d32', 0.1), color: '#2e7d32', fontWeight: 700 }}
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress.sentPercent}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    bgcolor: alpha('#2e7d32', 0.1),
                    '& .MuiLinearProgress-bar': { borderRadius: 6, bgcolor: '#2e7d32' }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SECTION 1: Received Images (View, Download & Delete) */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3, border: `1px solid ${alpha('#1565c0', 0.15)}` }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: alpha('#1565c0', 0.1), width: 40, height: 40 }}>
            <DownloadIcon sx={{ color: '#1565c0', fontSize: 22 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#1565c0">
              {t('ReceivedImages') || 'Received Images'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('ReceivedImagesDesc') || 'Images uploaded by the admin for design work'}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {receivedImages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, bgcolor: alpha('#000', 0.02), borderRadius: 2 }}>
            <ImageIcon sx={{ fontSize: 48, color: alpha('#000', 0.15), mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('NoReceivedImages') || 'No images received yet. Admin has not uploaded any images.'}
            </Typography>
          </Box>
        ) : (
          <ImageList cols={isMobile ? 2 : 4} gap={12}>
            {receivedImages.map((img, index) => (
              <ImageListItem
                key={img.Id || index}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${alpha('#000', 0.08)}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: `0 4px 12px ${alpha('#000', 0.1)}` }
                }}
              >
                <img
                  src={img.ImageUrl}
                  alt={`Received ${index + 1}`}
                  loading="lazy"
                  style={{ height: 180, objectFit: 'cover' }}
                  onClick={() => setPreviewDialog({
                    open: true,
                    imageUrl: img.ImageUrl,
                    title: `${t('ReceivedImage') || 'Received Image'} #${index + 1}`
                  })}
                />
                <ImageListItemBar
                  title={`#${index + 1}`}
                  subtitle={img.OriginalName || ''}
                  actionIcon={
                    <Stack direction="row" spacing={0.5} sx={{ mr: 0.5 }}>
                      <Tooltip title={t('Preview') || 'Preview'}>
                        <IconButton
                          size="small"
                          sx={{ color: '#fff' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewDialog({
                              open: true,
                              imageUrl: img.ImageUrl,
                              title: `${t('ReceivedImage') || 'Received Image'} #${index + 1}`
                            });
                          }}
                        >
                          <ZoomInIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('Download') || 'Download'}>
                        <IconButton
                          size="small"
                          sx={{ color: '#fff' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadImage(img.ImageUrl, img.OriginalName || `received-${index + 1}`);
                          }}
                        >
                          <SaveAltIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* ✅ NEW: Delete button for received images */}
                      {/* Backend allows delete if subscription is NOT completed */}
                      {!progress.isComplete && (
                        <Tooltip title={t('Delete') || 'Delete'}>
                          <IconButton
                            size="small"
                            sx={{ color: '#fff' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteReceivedImage(img.Id);
                            }}
                            disabled={deleteLoading === `recv-${img.Id}`}
                          >
                            {deleteLoading === `recv-${img.Id}` ? (
                              <CircularProgress size={16} sx={{ color: '#fff' }} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  }
                  sx={{
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)'
                  }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </Paper>

      {/* SECTION 2: Sent Images (Upload & Manage) */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: `1px solid ${alpha('#2e7d32', 0.15)}` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: alpha('#2e7d32', 0.1), width: 40, height: 40 }}>
              <SendIcon sx={{ color: '#2e7d32', fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#2e7d32">
                {t('SentDesigns') || 'Sent Designs'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('SentDesignsDesc') || 'Upload completed designs to send back to the admin'}
              </Typography>
            </Box>
          </Stack>

          {/* Upload Button */}
          {!progress.isComplete && (
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || progress.sent >= progress.total}
              sx={{
                bgcolor: '#2e7d32',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                '&:hover': { bgcolor: '#1b5e20' }
              }}
            >
              {uploading
                ? (t('Uploading') || 'Uploading...')
                : `${t('UploadDesigns') || 'Upload Designs'} (${progress.total - progress.sent} ${t('Remaining') || 'remaining'})`
              }
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleUploadSentImages}
            style={{ display: 'none' }}
          />
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {sentImages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, bgcolor: alpha('#000', 0.02), borderRadius: 2 }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: alpha('#000', 0.15), mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('NoSentDesigns') || 'No designs sent yet. Upload completed designs above.'}
            </Typography>
          </Box>
        ) : (
          <ImageList cols={isMobile ? 2 : 4} gap={12}>
            {sentImages.map((img, index) => (
              <ImageListItem
                key={img.Id || index}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${alpha('#2e7d32', 0.15)}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: `0 4px 12px ${alpha('#000', 0.1)}` }
                }}
              >
                <img
                  src={img.ImageUrl}
                  alt={`Design ${index + 1}`}
                  loading="lazy"
                  style={{ height: 180, objectFit: 'cover' }}
                  onClick={() => setPreviewDialog({
                    open: true,
                    imageUrl: img.ImageUrl,
                    title: `${t('SentDesign') || 'Sent Design'} #${index + 1}`
                  })}
                />
                <ImageListItemBar
                  title={`#${index + 1}`}
                  subtitle={img.OriginalName || ''}
                  actionIcon={
                    <Stack direction="row" spacing={0.5} sx={{ mr: 0.5 }}>
                      <Tooltip title={t('Preview') || 'Preview'}>
                        <IconButton
                          size="small"
                          sx={{ color: '#fff' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewDialog({
                              open: true,
                              imageUrl: img.ImageUrl,
                              title: `${t('SentDesign') || 'Sent Design'} #${index + 1}`
                            });
                          }}
                        >
                          <ZoomInIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!progress.isComplete && (
                        <Tooltip title={t('Delete') || 'Delete'}>
                          <IconButton
                            size="small"
                            sx={{ color: '#fff' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSentImage(img.Id);
                            }}
                            disabled={deleteLoading === img.Id}
                          >
                            {deleteLoading === img.Id ? (
                              <CircularProgress size={16} sx={{ color: '#fff' }} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  }
                  sx={{
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)'
                  }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="indeterminate"
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha('#2e7d32', 0.1),
                '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#2e7d32' }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
              {t('UploadingDesigns') || 'Uploading designs, please wait...'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, imageUrl: '', title: '' })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {previewDialog.title}
          <Stack direction="row" spacing={1}>
            <Tooltip title={t('Download') || 'Download'}>
              <IconButton
                onClick={() => handleDownloadImage(previewDialog.imageUrl, previewDialog.title)}
                sx={{ color: '#1565c0' }}
                              >
                <SaveAltIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => setPreviewDialog({ open: false, imageUrl: '', title: '' })}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {previewDialog.imageUrl && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <img
                src={previewDialog.imageUrl}
                alt={previewDialog.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '75vh',
                  objectFit: 'contain',
                  borderRadius: 8
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}